import { useEffect } from 'react';
import * as THREE from 'three';
import { focusOnObject } from '../pages/Dashboard/GridPage';
import store from '../redux/store';
// import { ActionCreators } from 'redux-undo';
import toast from 'react-hot-toast';

import { handlePasteCopy } from '../utils/UtilsFunction';
import {
  perspectiveToOrthographicZoom,
  VIEWPORT_CAMERA_FOV,
} from '../utils/ThreeJsUtils/viewportCameraSync';
import { copyObject } from '../redux/Slice/globalStates';
import { updateOpenModals } from '../redux/Slice/gridSlice';
import { selectAllObjects, getMultiSelection } from '../utils/selectAllObjects';
import { duplicateSelectedObjects } from '../utils/duplicateSelection';
import { isLiveSceneObject3D } from '../utils/ThreeJsUtils/assetVisibilityUtils';
import undoRedoManager from '../utils/UndoRedoManager';

import { useSelector } from 'react-redux';

// Module-level flag to prevent rapid Ctrl+S re-triggers.
// Using a module-level ref (not React state) so it is always fresh inside the
// keydown closure regardless of React's dependency cycle.
let _publishLock = false;

const useTransformControlsHotkeys = (objectt) => {
  const isPublishing = useSelector((state) => state.global_state.isPublishing);
  const {
    transformControls,
    selectedObject,
    camera,
    controlsOrbit,
    scene,
    Publish,
    setPublished,
    setName,
  } = objectt;

  useEffect(() => {
    const handleKeyDown = (event) => {
      const isCtrl = event.ctrlKey;
      const isShift = event.shiftKey;

      // ✅ Ctrl+S → Save/Publish — handled FIRST before any other guard so that
      //   event.preventDefault() always fires and the browser Save dialog never opens.
      if (isCtrl && !isShift && event.code === 'KeyS') {
        event.preventDefault();
        if (!isPublishing && !_publishLock && Publish && setPublished && setName) {
          _publishLock = true;
          Promise.resolve(
            Publish(
              setPublished,
              false,
              setName,
              objectt,
              objectt.animationObjects
            )
          ).finally(() => {
            _publishLock = false;
          });
        }
        return;
      }

      if (isPublishing || _publishLock) return;

      // ✅ Ctrl + Shift + F → Fullscreen (works in BOTH design and preview)
      if (isCtrl && isShift && event.code === 'KeyF') {
        toggleFullScreen();
        event.preventDefault();
        return;
      }

      // Disable all other hotkeys in preview mode
      const window_type = store.getState()?.window?.window_type;
      if (window_type === 'preview') return;

      if (!transformControls) return;

      // ✅ Handle camera reset (R without Ctrl/Shift)
      if (event.code === 'KeyR' && !isCtrl && !isShift) {
        resetCamera();
        event.preventDefault();
        return;
      }

      // 🔴 Delete key — open delete confirmation (not Ctrl+Delete)
      if (event.code === 'Delete' && !isCtrl && !isShift) {
        const activeElement = document.activeElement;
        const isTyping =
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          activeElement?.isContentEditable;

        if (!isTyping) {
          event.preventDefault();
          window.dispatchEvent(new CustomEvent('openDeleteModal'));
        }
        return;
      }

      // Shift+D only -> Duplicate (single or multi-selection)
      if (
        event.code === 'KeyD' &&
        isShift &&
        !isCtrl &&
        !event.metaKey &&
        !event.altKey
      ) {
        event.preventDefault();
        const multiSelection = getMultiSelection();
        const hasSelection =
          multiSelection.length > 0 ||
          (selectedObject && isLiveSceneObject3D(selectedObject));
        if (!hasSelection) {
          toast.error('Please select an object to duplicate.');
          return;
        }
        duplicateSelectedObjects(objectt);
        return;
      }

      // ✅ Ctrl/Cmd+A → Select all scene objects
      if ((isCtrl || event.metaKey) && !isShift && event.code === 'KeyA') {
        const activeElement = document.activeElement;
        const isTyping =
          activeElement?.tagName === 'INPUT' ||
          activeElement?.tagName === 'TEXTAREA' ||
          activeElement?.isContentEditable;
        if (!isTyping) {
          event.preventDefault();
          selectAllObjects({
            scene: objectt?.scene,
            transformControls,
            onPrimarySelected: objectt?.setSelectedObjectState,
            syncLocalSelection: objectt?.syncSelectAll,
          });
        }
        return;
      }

      // ✅ All other shortcuts require Ctrl
      if (!isCtrl) return;

      switch (event.code) {
        case 'KeyW':
          transformControls.setMode('translate');
          break;

        case 'KeyE':
          transformControls.setMode('rotate');
          break;

        case 'KeyR':
          transformControls.setMode('scale');
          break;

        case 'KeyC':
          store.dispatch(copyObject(selectedObject));
          break;

        case 'KeyV':
          handlePasteCopy(objectt);
          break;

        case 'KeyF':
          if (!selectedObject) {
            toast.error('Please select an object to focus.');
            break;
          }
          focusOnObject?.(selectedObject, camera, controlsOrbit, 1.5);
          break;

        case 'KeyZ':
          if (isCtrl && isShift) {
            event.preventDefault();
            undoRedoManager.redo();
          } else if (isCtrl && !isShift) {
            event.preventDefault();
            undoRedoManager.undo();
          }
          break;

        case 'KeyY':
          if (isCtrl && !isShift) {
            event.preventDefault();
            undoRedoManager.redo();
          }
          break;

        case 'KeyO':
          store.dispatch(updateOpenModals({ name: 'upload', isOpen: true }));
          event.preventDefault();
          break;

        default:
          break;
      }
    };

    const toggleFullScreen = () => {
      const element = document.documentElement;

      if (!document.fullscreenElement) {
        if (element.requestFullscreen) element.requestFullscreen();
        else if (element.webkitRequestFullscreen)
          element.webkitRequestFullscreen();
        else if (element.mozRequestFullScreen) element.mozRequestFullScreen();
        else if (element.msRequestFullscreen) element.msRequestFullscreen();
      } else {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      }
    };

    const resetCamera = () => {
      const { perspectiveCamera, orthographicCamera, currentCamera } = objectt;
      if (!perspectiveCamera || !orthographicCamera || !controlsOrbit) return;

      const target = new THREE.Vector3(0, 0, 0);
      perspectiveCamera.position.set(0, 2.5, 12);
      perspectiveCamera.fov = VIEWPORT_CAMERA_FOV;
      perspectiveCamera.lookAt(target);
      perspectiveCamera.updateProjectionMatrix();

      orthographicCamera.position.copy(perspectiveCamera.position);
      orthographicCamera.quaternion.copy(perspectiveCamera.quaternion);
      orthographicCamera.zoom = perspectiveToOrthographicZoom(
        perspectiveCamera,
        target,
        window.innerWidth,
        window.innerHeight
      );
      orthographicCamera.updateProjectionMatrix();

      controlsOrbit.target.copy(target);
      controlsOrbit.update();

      if (currentCamera?.isOrthographicCamera) {
        currentCamera.position.copy(orthographicCamera.position);
        currentCamera.zoom = orthographicCamera.zoom;
        currentCamera.updateProjectionMatrix();
      } else if (currentCamera) {
        currentCamera.position.copy(perspectiveCamera.position);
        currentCamera.updateProjectionMatrix?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    objectt,
    transformControls,
    selectedObject,
    camera,
    controlsOrbit,
    scene,
    Publish,
    setPublished,
    setName,
    isPublishing,
  ]);
};

export default useTransformControlsHotkeys;
